import React from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { noop } from 'lib/noop';

import { HORIZONTAL_MODE } from 'app/CanvasWidgets/const';
import isEqual from 'lodash/isEqual';
import { exportExcelFile } from 'utils/excelUtils';
import Switch from 'app/CanvasWidgets/components/Switch';
import Controls from './Controls';
import DynamicTable from './DynamicTable';

import styles from './TableWidgetModal.css';

/**
 * Widget which render table from input data
 */
export default class TableWidgetModal extends React.Component {
  static propTypes = {
    addNewSheet: PropTypes.func,
    closeModal: PropTypes.func,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        rows: PropTypes.arrayOf(
          PropTypes.shape({
            rowData: PropTypes.arrayOf(PropTypes.string),
            rowOptions: PropTypes.object
          })
        ),
        type: PropTypes.string
      })
    ),
    deleteSheet: PropTypes.func,
    onChange: PropTypes.func,
    fieldContract: PropTypes.shape({
      rowOptions: PropTypes.array
    }).isRequired,
    isOpen: PropTypes.bool,
    mode: PropTypes.string.isRequired,
    tableType: PropTypes.string,
    handleChangeMode: PropTypes.func.isRequired,
    renameSheet: PropTypes.func,
    metricsSystems: PropTypes.arrayOf(PropTypes.string)
  };

  static defaultProps = {
    addNewSheet: noop,
    deleteSheet: noop,
    renameSheet: noop,
    closeModal: noop,
    onChange: noop,
    tableType: undefined,
    data: {},
    isOpen: false,
    metricsSystems: []
  };

  /**
   * class constructor
   * @param {object} props - class proper ties
   */
  constructor(props) {
    super(props);

    this.handleRowCheckbox = this.handleRowCheckbox.bind(this);
    this.handleDiscardAndQuit = this.handleDiscardAndQuit.bind(this);
    this.handleSaveAndQuit = this.handleSaveAndQuit.bind(this);
    this.restoreData = this.restoreData.bind(this);
    this.spliceCol = this.spliceCol.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.spliceRow = this.spliceRow.bind(this);
    this.exportExcelFile = this.exportExcelFile.bind(this);
    this.triggerRadioChange = this.triggerRadioChange.bind(this);
    this.setNewName = this.setNewName.bind(this);
    this.openInputModal = this.openInputModal.bind(this);
    this.closeInputModal = this.closeInputModal.bind(this);
    this.generateNewItem = this.generateNewItem.bind(this);

    this.state = {
      data: [],
      mode: this.props.mode,
      metricRadioState: '',
      newSheetsName: '',
      isOpenModal: false,
      activeSheet: undefined,
      sheetModalType: undefined
    };
  }

  /**
   * react lifecycle method
   * @param {object} prevProps - previous props
   */
  componentDidUpdate(prevProps) {
    if (
      (!prevProps.isOpen && this.props.isOpen) ||
      !isEqual(prevProps.data, this.props.data)
    ) {
      this.restoreData();
    }
  }

  /**
   * function for updating Input value
   * @param {number} row - number of row
   * @param {number} col - number of col
   * @returns {Function} - updated state
   */
  handleChange(row, col) {
    return event => {
      const { value } = event.target;

      this.setState(prevState => {
        const data = { ...prevState.data };
        const input = data.rows[row].rowData[col];

        const currentMetricState =
          this.state.metricRadioState ||
          (this.props.metricsSystems && this.props.metricsSystems[0]);

        data.rows[row].rowData[col] = {
          ...input,
          ...{ [currentMetricState]: value }
        };

        return {
          ...prevState,
          data
        };
      });
    };
  }

  /**
   * set rows checkbox
   * @param {Event} event - event from checking checkbox
   */
  handleRowCheckbox(event) {
    const { checked, name } = event.target;

    this.setState(prevState => {
      const {
        data: { rows },
        selectedRow
      } = this.state;

      rows[selectedRow] = {
        ...rows[selectedRow],
        rowOptions: {
          ...rows[selectedRow].rowOptions,
          [name]: !!checked
        }
      };

      return {
        ...prevState,
        data: {
          ...prevState.data,
          rows
        }
      };
    });
  }

  /**
   * function which set active input in table
   * @param {number} row - number of row
   * @param {number} col - number of column
   * @returns {Function} - updated state
   */
  handleFocus(row, col) {
    return () => {
      this.setState(() => ({
        selectedRow: row,
        selectedCol: col
      }));
    };
  }

  /**
   * function for close modal with saved table
   */
  handleSaveAndQuit() {
    const {
      props: { onChange, closeModal },
      state: { data }
    } = this;
    onChange(data);
    closeModal();
  }

  /**
   * function for close modal without saving changes
   */
  handleDiscardAndQuit() {
    this.restoreData();
    this.props.closeModal();
  }

  /**
   * function that sets new Name for renaming or creating new sheet
   * @param {string} value - new name
   */
  setNewName(value) {
    this.setState({
      newSheetsName: value
    });
  }

  /**
   * function that set new switchers state when user click to new table
   * @param {string} value - value from Radio button
   */
  triggerRadioChange(value) {
    this.setState({
      metricRadioState: this.props.metricsSystems[parseInt(value, 10)]
    });
  }

  /**
   * function that generate new empty column item
   * @returns {{}} - object with fields that calls like sheets names
   * example output - {cm: '', inches: ''}
   */
  generateNewItem() {
    const updatedItem = {};
    this.props.metricsSystems.forEach(item => {
      updatedItem[item] = '';
    });
    return updatedItem;
  }

  /**
   * function which modified table rows
   * @param {string} direction - some rule for edit table
   * @returns {Function} - function for setting state with modified
   */
  spliceRow(direction) {
    const { rowOptions } = this.props.fieldContract;

    const defaultRowOptions = rowOptions.reduce(
      (acc, { name, defaultValue }) => ({
        ...acc,
        [name]: defaultValue
      }),
      {}
    );

    return () => {
      this.setState(prevState => {
        const {
          data: { rows },
          selectedRow
        } = prevState;

        const newRow = {
          rowData: rows[0].rowData.map(this.generateNewItem),
          rowOptions: defaultRowOptions
        };

        let newSelectedRow = selectedRow;

        switch (direction) {
          case 'delete': {
            rows.splice(selectedRow, 1);
            newSelectedRow = undefined;
            break;
          }
          case 'up': {
            rows.splice(selectedRow, 0, newRow);
            newSelectedRow = selectedRow + 1;
            break;
          }
          case 'down': {
            rows.splice(selectedRow + 1, 0, newRow);
            break;
          }
          case 'bottom': {
            rows.splice(rows.length, 0, newRow);
            break;
          }
          default:
            // eslint-disable-next-line no-console
            console.error('invalid row splice');
        }

        return {
          ...prevState,
          selectedRow: newSelectedRow
        };
      });
    };
  }

  /**
   * function which modified table columns
   * @param {string} direction - some rule for edit table
   * @returns {Function} - function for setting state with modified
   */
  spliceCol(direction) {
    const newItem = this.generateNewItem();
    return () => {
      this.setState(prevState => {
        const {
          data: { rows },
          selectedCol
        } = prevState;

        let newSelectedCol = selectedCol;

        rows.forEach(({ rowData }) => {
          switch (direction) {
            case 'delete':
              rowData.splice(selectedCol, 1);
              newSelectedCol = undefined;
              break;
            case 'left':
              rowData.splice(selectedCol, 0, newItem);
              newSelectedCol = selectedCol + 1;
              break;
            case 'right':
              rowData.splice(selectedCol + 1, 0, newItem);
              break;
            case 'end':
              rowData.splice(rowData.length, 0, newItem);
              break;
            default:
              // eslint-disable-next-line no-console
              console.error('invalid col splice');
          }
        });

        return {
          ...prevState,
          selectedCol: newSelectedCol
        };
      });
    };
  }

  /**
   * function that gets data from props and state and triggered export function
   */
  exportExcelFile() {
    const {
      props: { metricsSystems },
      state: {
        data: { rows }
      }
    } = this;

    exportExcelFile(metricsSystems, rows);
  }

  /**
   * function that opened input modal for input new sheets name or for creating new name for new sheet
   * @param {object} params - method params
   * @param {string} params.activeSheet - active sheet
   * @param {string} params.mode - operation
   */
  openInputModal({ activeSheet, mode }) {
    this.setState({
      isOpenModal: true,
      sheetModalType: mode,
      activeSheet
    });
  }

  /**
   * function that close input modal
   */
  closeInputModal() {
    this.setState({
      isOpenModal: false
    });
  }

  /**
   * function which get data from props and set parameter rows in state
   */
  restoreData() {
    const { data } = this.props;
    const {
      type = HORIZONTAL_MODE,
      rows = data.length
        ? data
        : [
            {
              rowData: [],
              rowOptions: {}
            }
          ]
    } = this.props.data;

    const { rowOptions } = this.props.fieldContract;

    /**
     * get row data
     * @param { Array } row - data to render new row
     * @returns {object} - rowOptions updated rowOptions in row
     */
    const getRowData = row => {
      const initialRowOptions = {};
      rowOptions.forEach(({ name, defaultValue }) => {
        const savedValue = row.rowOptions && row.rowOptions[name];

        // Use the defaultValue of the row option the savedValue is undefined
        initialRowOptions[name] = savedValue === undefined ? defaultValue : savedValue;
      });

      return {
        ...row,
        rowOptions: initialRowOptions
      };
    };

    this.setState({
      data: {
        type,
        rows: rows.map(getRowData)
      }
    });
  }

  /**
   * class method render
   * @returns {React.Component} return modal with widget
   */
  render() {
    const {
      state: {
        activeSheet,
        data: { rows = [] },
        selectedCol,
        selectedRow,
        metricRadioState,
        isOpenModal,
        newSheetsName,
        sheetModalType
      },
      props: {
        addNewSheet,
        closeModal,
        isOpen,
        mode,
        fieldContract,
        tableType,
        handleChangeMode,
        metricsSystems,
        renameSheet,
        deleteSheet
      },
      handleRowCheckbox,
      handleRowSelect,
      handleSaveAndQuit,
      handleDiscardAndQuit,

      spliceCol,
      spliceRow,
      exportExcelFile,
      handleChange,
      handleFocus,
      triggerRadioChange,
      openInputModal,
      closeInputModal,
      setNewName
    } = this;

    const radioContract = {
      allowedValues: metricsSystems
    };
    const currentMetricState = metricRadioState || (metricsSystems && metricsSystems[0]);
    const isShowSwitcher = metricsSystems.length > 1;

    return (
      <Modal className={styles.modal} isOpen={isOpen} toggle={closeModal} zIndex={5}>
        <ModalHeader toggle={closeModal}>Table Editor</ModalHeader>
        <ModalBody>
          <Controls
            addNewSheet={addNewSheet}
            handleRowCheckbox={handleRowCheckbox}
            handleRowSelect={handleRowSelect}
            handleSaveAndQuit={handleSaveAndQuit}
            handleDiscardAndQuit={handleDiscardAndQuit}
            rowOptions={fieldContract.rowOptions}
            rows={rows}
            tabs={metricsSystems}
            selectedCol={selectedCol}
            selectedRow={selectedRow}
            spliceCol={spliceCol}
            spliceRow={spliceRow}
            mode={mode}
            exportExcelFile={exportExcelFile}
            renameSheet={renameSheet}
            isOpen={isOpenModal}
            openInputModal={openInputModal}
            closeInputModal={closeInputModal}
            newSheetsName={newSheetsName}
            handleChangeMode={handleChangeMode}
            setNewName={setNewName}
            sheetModalType={sheetModalType}
            activeSheet={activeSheet}
            deleteSheet={deleteSheet}
          />
          {rows.length && (
            <div className={styles.data}>
              <DynamicTable
                rows={rows}
                mode={mode}
                tableType={tableType}
                activeTable={currentMetricState}
                handleChange={handleChange}
                handleFocus={handleFocus}
                tablesNames={metricsSystems}
              />
              {isShowSwitcher && (
                <div className={styles.radioBlock}>
                  <Switch
                    onChange={triggerRadioChange}
                    selectedValue={currentMetricState}
                    fieldContract={radioContract}
                    isRadioOn={false}
                  />
                </div>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  }
}
